import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper } from '@tanstack/react-table'
import { skipToken } from '@reduxjs/toolkit/query'

import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  styled,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import Table from '@/components/ui/tables/Table'

import { useGetNetworkInvitationsQuery, useGetNetworkQuery } from '@/core/services/network'
import { useLazyGetStaffsByEstablishmentQuery } from '@/core/services/staff'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

const CustomPaper = styled(Paper)({
  borderRadius: 0,
})

type InviteStaffData = {
  id: string
  disabled?: boolean
  name: string
  email: string
  status: string
}

type InviteStaffsDialogProps = Readonly<{
  isSending?: boolean
  networkId: string
  open: boolean
  onClose: () => void
  onSend: (staffs: string[]) => void
}>

const columnHelpers = createColumnHelper<InviteStaffData>()

export default function InviteStaffsDialog(props: InviteStaffsDialogProps) {
  const { isSending = false, networkId, open, onClose, onSend } = props
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [invitedStaffs, setInvitedStaffs] = useState<InviteStaffData[]>([])

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useGetNetworkQuery(networkId)
  const { data: networkInvitations } = useGetNetworkInvitationsQuery(networkId ?? skipToken)
  const [getStaffsByEstablishment, { data: staffs }] = useLazyGetStaffsByEstablishmentQuery()

  const columns = useMemo(() => [columnHelpers.accessor('name', {}), columnHelpers.accessor('email', {}), columnHelpers.accessor('status', {})], [])

  const staffsData = useMemo(
    () =>
      staffs
        ?.filter((staff) => staff._id !== authUser?._id && staff._id !== network?.createdBy._id && !network?.members.find((member) => member === staff._id))
        .map((staff) => ({
          id: staff._id,
          disabled: !!networkInvitations?.find((networkInvitation) => networkInvitation.invitee === staff._id),
          name: `${staff.userInfo.name} ${staff.userInfo.surname}`,
          email: staff.email,
          status: isStaff(staff) ? staff.status : '',
        })) ?? [],
    [authUser, network, networkInvitations, staffs]
  )

  useEffect(() => {
    if (authUser?.establishment && open) {
      getStaffsByEstablishment(authUser.establishment._id)
    }
  }, [authUser, getStaffsByEstablishment, open])

  useEffect(() => {
    if (!open) {
      setInvitedStaffs([])
    }
  }, [open])

  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog fullWidth maxWidth="lg" onClose={handleClose} open={open} sx={{ height: 'auto' }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{t('dialog.networkInviteStaffs.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container>
          <Grid item xs={isMobile ? 12 : 8}>
            <Box>
              <Table
                autoResetPage={false}
                container={CustomPaper}
                columns={columns}
                data={staffsData}
                disableHeader
                onSelectRow={setInvitedStaffs}
                selectable
              />
            </Box>
          </Grid>
          {!isMobile && <Grid item xs={4}>
            <Box height="100%" sx={{ position: 'relative', overflowX: 'hidden', overflowY: 'auto' }}>
              <List sx={{ position: 'absolute', width: '100%' }}>
                {invitedStaffs.map((staff) => (
                  <ListItem
                    key={staff.id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setInvitedStaffs((prevStaffs) => prevStaffs.filter((prevStaff) => prevStaff.id !== staff.id))
                        }}
                      >
                        <CloseRoundedIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar />
                    </ListItemAvatar>
                    <ListItemText primary={staff.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <Button onClick={handleClose} variant="neutral">
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton
          loading={isSending}
          onClick={() => {
            onSend(_.map(invitedStaffs, 'id'))
          }}
          variant="contained"
        >
          {t('common:button.send.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
